import { type FocusEvent, type ReactNode, useRef, useState } from "react";
import { COPY, INLINE_TEXT_INPUT_CLASS } from "@/constants/campaignContacts";
import type { CampaignContactRow } from "./types";
import { useInlineCommit } from "./useInlineCommit";

interface ContactNameEditorProps {
  contact: CampaignContactRow;
  readOnly: boolean;
  /** Native tooltip for the editor. */
  title?: string;
  /** Called with trimmed values; a rejection makes the same pair retryable on the next blur. */
  onCommit: (
    contact: CampaignContactRow,
    firstName: string,
    lastName: string,
  ) => void | Promise<void>;
  /** Called with the refusal message when the first name is blank. */
  onInvalid: (message: string) => void;
  /** Class for both `<td>`s, so the cells match the rest of the row. */
  cellClassName: string;
  /** Rendered after the first name input, inside the First Name cell (the NEW tag). */
  firstCellExtra?: ReactNode;
}

interface ContactNameCellsProps {
  contact: CampaignContactRow;
  /** Native tooltip, carried by both cells. */
  title?: string;
  cellClassName: string;
  firstCellExtra?: ReactNode;
}

/**
 * First Name cell body: control and NEW tag side by side, no gap (the tag brings its own margin).
 * The 110px floor matches the inline inputs' and holds the read-only cells; an input keeps its own
 * floor, so a NEW tag widens the column rather than squeezing the input.
 */
const FIRST_CELL_ROW_CLASS = "flex min-w-[110px] items-center";

const pairKey = (first: string, last: string) => `${first}\u0000${last}`;

/** True when focus moves from a name input to somewhere outside the pair (or nowhere). */
export function leavesNamePair(related: unknown, pair: readonly unknown[]): boolean {
  return related == null || !pair.includes(related);
}

/**
 * Read-only First Name and Last Name cells for one recipient row. A row without split names shows
 * its full name in the first cell and leaves the last one empty.
 */
export function ContactNameCells({
  contact,
  title,
  cellClassName,
  firstCellExtra,
}: ContactNameCellsProps) {
  return (
    <>
      <td className={cellClassName} title={title}>
        <div className={FIRST_CELL_ROW_CLASS}>
          <span className="min-w-0 flex-1 truncate">{contact.firstName ?? contact.name}</span>
          {firstCellExtra ? <span className="shrink-0">{firstCellExtra}</span> : null}
        </div>
      </td>
      <td className={cellClassName} title={title}>
        {contact.firstName === undefined ? "" : (contact.lastName ?? "")}
      </td>
    </>
  );
}

/**
 * Inline first/last name inputs for one recipient row, one per table cell. Owns its drafts and,
 * through `useInlineCommit`, the last committed pair (the row prop stays stale until the refetch
 * lands). Commits when focus leaves both inputs, not when it moves between them.
 */
export default function ContactNameEditor({
  contact,
  readOnly,
  title,
  onCommit,
  onInvalid,
  cellClassName,
  firstCellExtra,
}: ContactNameEditorProps) {
  const firstRef = useRef<HTMLInputElement>(null);
  const lastRef = useRef<HTMLInputElement>(null);
  const [first, setFirst] = useState(contact.firstName ?? "");
  const [last, setLast] = useState(contact.lastName ?? "");
  const inline = useInlineCommit(pairKey(contact.firstName ?? "", contact.lastName ?? ""));

  async function handleBlur(event: FocusEvent<HTMLInputElement>) {
    if (!leavesNamePair(event.relatedTarget, [firstRef.current, lastRef.current])) return;
    const nextFirst = first.trim();
    const nextLast = last.trim();
    const pair = pairKey(nextFirst, nextLast);
    if (pair === inline.baseline()) return;
    if (nextFirst === "") return onInvalid(COPY.toast.nameRequired);
    await inline.commit(pair, () => onCommit(contact, nextFirst, nextLast));
  }

  return (
    <>
      <td className={cellClassName}>
        <div className={FIRST_CELL_ROW_CLASS}>
          <input
            ref={firstRef}
            type="text"
            value={first}
            readOnly={readOnly}
            title={title}
            aria-label={COPY.actions.firstNameLabel(contact.name)}
            onChange={(event) => setFirst(event.target.value)}
            onBlur={handleBlur}
            className={`${INLINE_TEXT_INPUT_CLASS} flex-1`}
          />
          {firstCellExtra ? <span className="shrink-0">{firstCellExtra}</span> : null}
        </div>
      </td>
      <td className={cellClassName}>
        <input
          ref={lastRef}
          type="text"
          value={last}
          readOnly={readOnly}
          title={title}
          aria-label={COPY.actions.lastNameLabel(contact.name)}
          onChange={(event) => setLast(event.target.value)}
          onBlur={handleBlur}
          className={INLINE_TEXT_INPUT_CLASS}
        />
      </td>
    </>
  );
}
